import { Component, computed, DestroyRef, effect, ElementRef, inject, signal, viewChild } from '@angular/core';
import {
  DynamicRouteHandlingService,
  DropdownData,
  FileUploadService,
  GenericDropdownComponent,
  InputFieldComponent,
  LookupsService,
  rxResourceFixed
} from '@zayan-erp/shared';
import { TemplateDocumentService } from '../../services/template-document.service';
import { map, of, switchMap } from 'rxjs';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TEMPLATE_TYPE_LABELS, TemplateType } from '../../models/template.models';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { selectProjects } from '@zayan-erp/stores';

@Component({
  selector: 'lib-add-master-template',
  imports: [GenericDropdownComponent, InputFieldComponent, ReactiveFormsModule, NgbTooltip],
  templateUrl: './add-master-template.component.html',
  standalone: true
})
export class AddMasterTemplateComponent {
  #templateService = inject(TemplateDocumentService);
  #fileUploadService = inject(FileUploadService);
  #lookupsService = inject(LookupsService);
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #dynamicRouteHandling = inject(DynamicRouteHandlingService);
  #destroyRef = inject(DestroyRef);
  #store = inject(Store);

  templateVariables = toSignal(this.#lookupsService.readTemplateVariables(), { initialValue: [] });
  projectId = toSignal(this.#route.params.pipe(map(params => params['projectId'])), { requireSync: true });
  templateId = toSignal(this.#route.params.pipe(map(params => params['templateId'])), { requireSync: true });

  isReplaceMode = computed(() => !!this.templateId());

  projects = toSignal(this.#store.select(selectProjects), { initialValue: [] });

  documentTypeOptions: DropdownData[] = Object.values(TemplateType).map(type => ({
    id: type,
    firstLabel: TEMPLATE_TYPE_LABELS[type]
  }));

  existingTemplateResource = rxResourceFixed({
    params: () => ({ id: this.templateId() }),
    defaultValue: null,
    stream: ({ params }) => (params.id ? this.#templateService.readTemplateDocument(params.id) : of(null))
  });

  fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  form = new FormGroup({
    s3DocumentId: new FormControl<string | null>(null),
    name: new FormControl<string | null>(null, Validators.required),
    templateVariableId: new FormControl<string | null>(null, Validators.required),
    documentType: new FormControl<string | null>(TemplateType.APS, Validators.required),
    projectId: new FormControl<string | null>(null, Validators.required),
    sequence: new FormControl<number | null>(null)
  });

  selectedFile = signal<File | null>(null);
  isUploadFileError = signal<boolean>(false);
  selectedFileName = computed(() => this.selectedFile()?.name ?? null);

  ngOnInit() {
    this.#dynamicRouteHandling.submitButton$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.onSubmit();
    });
  }

  onTemplateVariablesReceived = effect(() => {
    if (this.templateVariables() && this.templateVariables().length > 0) {
      this.form.controls.templateVariableId.setValue(this.templateVariables()[0].id);
    }
  });

  onProjectIdReceived = effect(() => {
    if (this.projectId()) {
      this.form.controls.projectId.setValue(this.projectId());
    }
  });

  onExistingTemplateLoaded = effect(() => {
    const template = this.existingTemplateResource.value();
    if (!template) return;
    this.form.patchValue({
      name: template.name,
      documentType: template.documentType,
      projectId: template.projectId,
      sequence: template.sequence,
      templateVariableId: template.templateVariable?.id ?? null
    });
  });

  onFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.isUploadFileError.set(false);
      this.selectedFile.set(file);
    }
  }

  removeSelectedFile(event: Event) {
    event.stopPropagation();

    this.selectedFile.set(null);

    const input = this.fileInput();
    if (input?.nativeElement) {
      input.nativeElement.value = '';
    }
  }

  private uploadFile(file: File) {
    return this.#fileUploadService.generatePublicUploadUrl(file.name).pipe(
      switchMap((uploadUrlResult: any) =>
        this.#fileUploadService
          .uploadFileToS3WithFetch({
            fileName: uploadUrlResult.fileName,
            presignedUrl: uploadUrlResult.uploadUrl,
            s3Key: uploadUrlResult.s3Key,
            sourceFile: file
          })
          .pipe(map(() => uploadUrlResult.documentId))
      )
    );
  }

  handleSubmit() {
    this.#dynamicRouteHandling.submitButton.next();
  }

  onSubmit() {
    this.isUploadFileError.set(false);
    if (this.form.invalid || !this.selectedFile()) {
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity();
      if (!this.selectedFile()) {
        this.isUploadFileError.set(true);
      }
      return;
    }

    const file = this.selectedFile()!;

    this.uploadFile(file)
      .pipe(
        switchMap((documentId: string) => {
          this.form.controls.s3DocumentId.setValue(documentId);
          return this.saveOrUpdateTemplate();
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: () => this.#onSaveSuccess()
      });
  }

  #onSaveSuccess() {
    this.#dynamicRouteHandling.reload.next(!this.#dynamicRouteHandling.reload.value);
    const returnRoute = this.#route.snapshot.data['returnRoute'];
    this.#router.navigate([returnRoute ?? 'home-building/template-document/projects']);
  }

  private saveOrUpdateTemplate() {
    if (this.form.invalid) {
      return of(null);
    }

    const { s3DocumentId, name, templateVariableId, documentType, projectId, sequence } = this.form.value;

    const payload = {
      s3DocumentId,
      name,
      documentType,
      templateVariableId,
      projectId,
      sequence: sequence ?? null
    };

    const templateId = this.templateId();
    if (templateId) {
      return this.#templateService.updateTemplateDocument(templateId, payload as any);
    }

    return this.#templateService.saveTemplateDocument(payload);
  }
}
